export const DAYS_NAMES = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo', 'Eventual']
export const MONTH_NAMES = [
  'Enero',    // 0
  'Febrero',  // 1
  'Marzo',    // 2
  'Abril',    // 3
  'Mayo',     // 4
  'Junio',    // 5
  'Julio',    // 6
  'Agosto',   // 7
  'Septiembre', // 8
  'Octubre',  // 9
  'Noviembre', // 10
  'Diciembre' // 11
];
/**
 * Parses a date string into a Date object based on the provided format.
 * @param {string} dateString - The date string to be parsed.
 * @param {string} format - The format of the date string (e.g., 'YYYY-MM-DD HH:mm:SS').
 * @returns {Date | null} - The parsed Date object, or null if the input is invalid.
 */
export function stringToDateTime(dateString: string, format: string): Date | null {
  if (!dateString || !format) {
    throw new Error('Invalid Date string');
  }

  const formatRegex = format
    .replace('YYYY', '(\\d{4})')
    .replace('MM', '(\\d{2})')
    .replace('DD', '(\\d{2})')
    .replace('HH', '(\\d{2})')
    .replace('mm', '(\\d{2})')
    .replace('SS', '(\\d{2})');

  const regex = new RegExp(`^${formatRegex}$`);
  const match = dateString.match(regex);

  if (!match) {
    throw new Error("Input doesn't match the format");
  }

  // Map the format tokens to their respective match groups
  const year = format.indexOf('YYYY') !== -1 ?
    parseInt(match[format.split('YYYY')[0].split(/[^A-Za-z]/).length], 10)
    : 0;
  const month = format.indexOf('MM') !== -1 ?
    parseInt(match[format.split('MM')[0].split(/[^A-Za-z]/).length], 10) - 1
    : 0; // Months are 0-indexed
  const day = format.indexOf('DD') !== -1 ?
    parseInt(match[format.split('DD')[0].split(/[^A-Za-z]/).length], 10)
    : 1;
  const hour = format.indexOf('HH') !== -1 ?
    parseInt(match[format.split('HH')[0].split(/[^A-Za-z]/).length], 10)
    : 0;
  const minutes = format.indexOf('mm') !== -1 ?
    parseInt(match[format.split('mm')[0].split(/[^A-Za-z]/).length], 10)
    : 0;
  const seconds = format.indexOf('SS') !== -1 ?
    parseInt(match[format.split('SS')[0].split(/[^A-Za-z]/).length], 10)
    : 0;

  // Validate ranges
  if (
    month < 0 || month > 11 ||
    day < 1 || day > 31 ||
    hour < 0 || hour > 23 ||
    minutes < 0 || minutes > 59 ||
    seconds < 0 || seconds > 59
  ) {
    throw new Error('Invalid month, day, hour, minutes or seconds number.');
  }

  return new Date(year, month, day, hour, minutes, seconds);
}


/**
 * Formats a Date object into a string based on the provided format.
 * @param {Date} date - The Date object to be formatted.
 * @param {string} format - The desired format for the output string (e.g., 'YYYY-MM-DD HH:mm:SS').
 * @returns {string} - The formatted date string.
 */
export function dateTimeToString(date: Date, format: string): string {
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    throw new Error('Invalid Date object');
  }

  const components = {
    YYYY: date.getFullYear().toString(),
    MM: (date.getMonth() + 1).toString().padStart(2, '0'), // Months are 0-indexed
    DD: date.getDate().toString().padStart(2, '0'),
    HH: date.getHours().toString().padStart(2, '0'),
    mm: date.getMinutes().toString().padStart(2, '0'),
    SS: date.getSeconds().toString().padStart(2, '0'),
  };

  // Replace format tokens with actual values
  let formattedDate = format;
  for (const [key, value] of Object.entries(components)) {
    formattedDate = formattedDate.replace(key, value);
  }

  return formattedDate;
}


/**
 * Converts a date string from one format to another.
 * @param {string} dateString - The input date string.
 * @param {string} inputFormat - The format of the input date string (e.g., 'DD/MM/YYYY').
 * @param {string} outputFormat - The desired format for the output string (e.g., 'YYYY-MM-DD').
 * @returns {string | null} - The formatted date string, or null if the input is invalid.
 */
export function dateTimeStrToAnother(
  dateString: string,
  inputFormat: string,
  outputFormat: string
): string | null {
  const date = stringToDateTime(dateString, inputFormat);
  if (!date) {
    throw new Error('Invalid date string or format');
  }
  return dateTimeToString(date, outputFormat);
}


/**
 * Formats the value of an input date control to a 'YYYY-MM-DD' string.
 * @param {string | Date} date - The date value to be formatted.
 * @returns {string} - The formatted date string in 'YYYY-MM-DD' format.
 */
export function formatInputDateControl(date: string | Date): string {
  return new Date(date).toISOString().split('T')[0];
}


/**
 * Calculates the number of days between two dates.
 * @param {Date} startDate - The starting date.
 * @param {Date} endDate - The ending date.
 * @returns {number} - The number of days between the two dates.
 */
export function getDaysDifference(startDate: Date, endDate: Date): number {
  if (!startDate || !endDate) {
    return 0;
  }

  const timeDifference = endDate.getTime() - startDate.getTime();
  return timeDifference / (1000 * 60 * 60 * 24);
}

/**
 * Adds a specified number of hours to a date.
 * @param {Date} date - The original date.
 * @param {number} hours - The number of hours to add.
 * @returns {Date} - The new Date object with the added hours.
 */
export function addHours(date: Date, hours: number): Date {
  const newDate = new Date(date);
  newDate.setHours(newDate.getHours() + hours);
  return newDate;
}

/**
 * Adds a specified number of days to a date.
 * @param {Date} date - The original date.
 * @param {number} days - The number of days to add.
 * @returns {Date} - The new Date object with the added days.
 */
export function addDays(date: Date, days: number): Date {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() + days);
  return newDate;
}
